/**
 * utils/notificationCenter.js — history for the top-right toasts.
 *
 * The toasts themselves are `sonner`, scattered across ~50 files. Rather
 * than editing every call site, we wrap the shared `toast` object once at
 * startup: every existing and future notification is captured for free.
 *
 * Storage, per the owner's choice:
 *   • browser  — last 200 notifications of every type (localStorage)
 *   • server   — errors only, last 100 per account (survives a cleared
 *                browser or another device)
 */
import axios from "axios";
import { toast } from "sonner";

const STORAGE_KEY = "etf.notifications.v1";
const MAX_LOCAL = 200;
const CHANGED_EVENT = "notifications:changed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = ["success", "error", "info", "warning", "message"];

/**
 * Identical notifications raised within this window are one event.
 *
 * Real case: when a session dies, every request already in flight comes
 * back 401 at once and each one asked for the same toast — the panel filled
 * up with five copies of "Session expired". Now the first one wins and the
 * repeats only bump its counter.
 */
const DEDUPE_WINDOW_MS = 5000;
const DUPLICATE = Symbol("duplicate-notification");

/**
 * Dedicated client for the history endpoints.
 *
 * Created with `axios.create`, so it skips the global response interceptor:
 * a 401 here must never raise a "session expired" toast, or the history
 * would generate the very noise it exists to record. It also carries the
 * break-glass Bearer token when the admin console is the caller.
 */
export const notificationsClient = axios.create({ withCredentials: true });

notificationsClient.interceptors.request.use((config) => {
  const token = window.sessionStorage.getItem("admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function readAll() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_LOCAL)));
  } catch {
    /* quota or private mode — the toast itself still works */
  }
}

/** What the user was doing: the panel is useless without this context. */
function currentContext() {
  const { pathname, hash, search } = window.location;
  return `${pathname}${search || ""}${hash || ""}`;
}

/** Pull a human string out of whatever a call site passed to sonner. */
function textOf(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  // React elements and objects: don't try to render them, just say so.
  return "(notifica non testuale)";
}

export function listNotifications() {
  return readAll();
}

/**
 * Read state lives on the entries themselves.
 *
 * A "last read at" timestamp looked simpler but silently lost anything
 * raised in the same millisecond the panel was opened.
 */
export function markAllRead() {
  const list = readAll();
  if (list.some((n) => !n.read)) {
    writeAll(list.map((n) => ({ ...n, read: true })));
  }
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

export function unreadCount() {
  return readAll().filter((n) => !n.read).length;
}

export function clearLocalNotifications() {
  writeAll([]);
  markAllRead();
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

export function onNotificationsChanged(handler) {
  window.addEventListener(CHANGED_EVENT, handler);
  return () => window.removeEventListener(CHANGED_EVENT, handler);
}

function record(type, message, options) {
  const text = textOf(message);
  const description = textOf(options?.description) || null;
  const list = readAll();

  const twinIndex = list.findIndex(
    (n) =>
      n.type === type &&
      n.message === text &&
      (n.description || null) === description &&
      Date.now() - new Date(n.at).getTime() < DEDUPE_WINDOW_MS,
  );
  if (twinIndex !== -1) {
    const twin = list[twinIndex];
    const merged = { ...twin, count: (twin.count || 1) + 1, read: false };
    const next = [...list];
    next[twinIndex] = merged;
    writeAll(next);
    window.dispatchEvent(new Event(CHANGED_EVENT));
    return DUPLICATE;
  }

  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    message: text,
    description,
    page: currentContext(),
    at: new Date().toISOString(),
    read: false,
  };
  writeAll([entry, ...list]);
  window.dispatchEvent(new Event(CHANGED_EVENT));

  if (type === "error") {
    // Fire and forget: a failing history write must never itself toast.
    notificationsClient.post(`${API}/notifications`, {
      id: entry.id,
      type: entry.type,
      message: entry.message,
      description: entry.description,
      page: entry.page,
    }).catch(() => {});
  }
  return entry;
}

let installed = false;

/**
 * Wrap the shared sonner `toast` object in place.
 *
 * Every module imported `toast` from the same instance, so patching the
 * methods here reaches all ~350 call sites without touching them.
 */
export function installNotificationCapture() {
  if (installed) return;
  installed = true;
  KINDS.forEach((kind) => {
    const original = toast[kind];
    if (typeof original !== "function") return;
    toast[kind] = (message, options) => {
      let outcome;
      try {
        outcome = record(kind, message, options);
      } catch {
        /* never let bookkeeping break a notification */
      }
      // A burst repeat is already on screen: showing it again is the noise
      // the owner asked us to remove.
      if (outcome === DUPLICATE) return undefined;
      return original(message, options);
    };
  });
}
